import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../api/client';

const MAX_MONTHS_AHEAD = 24;

function monthKey(doctorId: string, serviceId: string, year: number, month: number) {
  return ['availability', doctorId, serviceId, year, month] as const;
}

/**
 * Lazy-loaded month availability with prefetching of the next month so
 * navigating forward feels instant. React Query caches per-month so going
 * back to a previously-viewed month is also instant.
 */
export function useAvailability(
  doctorId: string,
  serviceId: string | null,
  year: number,
  month: number,
  /* minutos de la cita: con depilación por zonas, cambia lo que hay libre */
  minutos?: number,
) {
  const qc = useQueryClient();
  const enabled = !!serviceId && isWithinRange(year, month);

  const query = useQuery({
    queryKey: serviceId
      ? [...monthKey(doctorId, serviceId, year, month), minutos ?? 0]
      : (['availability', 'disabled'] as const),
    queryFn: () => api.availability(doctorId, serviceId!, year, month, minutos),
    enabled,
    staleTime: 30_000, // slots can be claimed by others
    refetchOnWindowFocus: true,
  });

  // Prefetch next month so forward navigation feels instant.
  useEffect(() => {
    if (!serviceId) return;
    const { year: ny, month: nm } = addMonths(year, month, 1);
    if (!isWithinRange(ny, nm)) return;
    qc.prefetchQuery({
      queryKey: [...monthKey(doctorId, serviceId, ny, nm), minutos ?? 0],
      queryFn: () => api.availability(doctorId, serviceId, ny, nm, minutos),
      staleTime: 30_000,
    });
  }, [doctorId, serviceId, year, month, minutos, qc]);

  return query;
}

export function isWithinRange(year: number, month: number): boolean {
  const now = new Date();
  const target = new Date(year, month - 1, 1);
  const minDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const maxDate = new Date(now.getFullYear(), now.getMonth() + MAX_MONTHS_AHEAD, 1);
  return target >= minDate && target <= maxDate;
}

export function addMonths(year: number, month: number, delta: number) {
  const d = new Date(year, month - 1 + delta, 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}
